import { ClaimsSchedule } from '@Components/claims/claims'
import { ClaimFormSearch } from '@Components/claims/claim-form-search'
import { ClaimsAdmin } from '@Components/claims/claims-admin'
import { RolesList } from '@Components/roles/roles-list'
import { UpsertRole } from '@Components/roles/upsert-role'
import { CouponHistory } from '@Components/tools/player/coupon-history'
import { PlayerDetails } from '@Components/tools/player/player-details'
import { PlayerDetailsRoot } from '@Components/tools/player/player-details-root'
import { PlayerSearch } from '@Components/tools/player/player-search'
import { ScanHistory } from '@Components/tools/player/scan-history'
import { UpsertUser } from '@Components/users/upsert-user'
import { Users } from '@Components/users/users'
import { UsersList } from '@Components/users/users-list'
import { AuthScopes, FeatureFlag } from '@Generated/back-office-client/src'
import { Outlet } from 'react-router-dom'
import { ParentRouteConfig } from './route-mapping'
import { Routes } from './types'
import { SearchAnalytics } from '@Components/reporting/analytics/search-analytics'
import { ClaimsReports } from '@Components/reporting/claims/claims-reports'
import { CouponsAdmin } from '@Components/coupons/coupons-admin'
import { CreateCouponPromotion } from '@Components/coupons/create-coupon-promotion'
import { SearchConfiguration } from '@Components/search/search-configuration'
import { SpinToWinHistory } from '@Components/tools/player/spin-to-win-history'
import { ImportHistory } from '@Components/imports/import-history'
import { ImportHistoryDetails } from '@Components/imports/import-history-details'
import { PluginConfiguration } from '@Components/plugins/plugin-configuration'
import { ApiKeys } from '@Components/api-keys/api-keys'
import { ApiKeysList } from '@Components/api-keys/api-keys-list'
import { CreateApiKey } from '@Components/api-keys/create-api-key'
import { EditApiKey } from '@Components/api-keys/edit-api-key'
import { PromotionCampaignsAdmin } from '@Components/promotion-campaigns/promotion-campaigns-admin'
import { TicketScanSearch } from '@Components/tools/ticket-scan-search/ticket-scan-search'
import { ScannerScheduledDowntime } from '@Components/tools/scanner-scheduled-downtime/scanner-scheduled-downtime'
import { CustomerConfig } from '@Components/customer-config/customer-config'

export const enum RouteGroup {
  PLAYER_MANAGEMENT = 'Player Management',
  SYSTEM_OPERATIONS = 'System Operations',
  REPORTING = 'Reporting',
  ADMIN = 'Admin',
}

export const getRouteList = (): ParentRouteConfig[] => {
  return [
    {
      routerConfig: { path: 'players', Component: Outlet },
      routeGroup: RouteGroup.PLAYER_MANAGEMENT,
      scope: AuthScopes.readplayer_details,
      childrenRoutes: [
        {
          routerConfig: { path: '', Component: PlayerSearch }, // /players
          submenuDisplayName: 'Player Lookup',
          submenuRoute: Routes.PLAYER_SEARCH,
          scope: AuthScopes.readplayer_details,
        },
        {
          routerConfig: { path: ':lookupId', Component: PlayerDetailsRoot }, // /players/:lookupId
          scope: AuthScopes.readplayer_details,
          childrenRoutes: [
            {
              routerConfig: { path: '', Component: PlayerDetails }, // /players/:lookupId
              scope: AuthScopes.readplayer_details,
            },
            {
              routerConfig: { path: 'coupons', Component: CouponHistory }, // /players/:lookupId/coupons
              scope: AuthScopes.readplayer_coupons,
              feature: FeatureFlag.coupons,
            },
            {
              routerConfig: { path: 'scans', Component: ScanHistory }, // /players/:lookupId/scans
              scope: AuthScopes.readplayer_scans,
            },
            {
              routerConfig: { path: 'spins', Component: SpinToWinHistory }, // /players/:lookupId/spins
              scope: AuthScopes.readplayer_spins,
              feature: FeatureFlag.spin_to_win,
            },
          ],
        },
      ],
    },

    {
      routerConfig: { path: 'claims', Component: Outlet }, // /claims
      routeGroup: RouteGroup.PLAYER_MANAGEMENT,
      scope: AuthScopes.readclaim_center_reservations,
      childrenRoutes: [
        {
          routerConfig: { path: '', Component: ClaimsSchedule },
          submenuDisplayName: 'Claim Appointments',
          submenuRoute: Routes.CLAIMS,
          scope: AuthScopes.readclaim_center_reservations,
        },
      ],
    },
    {
      routerConfig: { path: 'claim-forms', Component: Outlet }, // /claim-forms
      routeGroup: RouteGroup.PLAYER_MANAGEMENT,
      scope: AuthScopes.readclaim_center_reservations,
      feature: FeatureFlag.claim_forms,
      childrenRoutes: [
        {
          routerConfig: { path: '', Component: ClaimFormSearch },
          submenuDisplayName: 'Claim Forms',
          submenuRoute: Routes.CLAIM_FORMS,
          scope: AuthScopes.readclaim_center_reservations,
        },
      ],
    },
    {
      routerConfig: { path: 'claims-admin', Component: Outlet }, // /claims-admin
      routeGroup: RouteGroup.SYSTEM_OPERATIONS,
      scope: AuthScopes.writeclaim_center_admin,
      childrenRoutes: [
        {
          routerConfig: { path: '', Component: ClaimsAdmin },
          submenuDisplayName: 'Claim Center Setup',
          submenuRoute: Routes.CLAIMS_ADMIN,
          scope: AuthScopes.writeclaim_center_admin,
        },
      ],
    },
    {
      routerConfig: { path: 'coupons-admin', Component: Outlet }, // /coupons-admin
      routeGroup: RouteGroup.SYSTEM_OPERATIONS,
      scope: AuthScopes.readcoupons,
      childrenRoutes: [
        {
          routerConfig: { path: '', Component: CouponsAdmin },
          submenuDisplayName: 'Coupons Admin',
          submenuRoute: Routes.COUPON_PROMOTIONS,
          scope: AuthScopes.readcoupons,
        },
        {
          routerConfig: { path: 'create', Component: CreateCouponPromotion }, //coupons-admin/create
          scope: AuthScopes.writecoupons,
        },
      ],
    },
    {
      routerConfig: { path: 'promotion-campaigns', Component: Outlet },
      routeGroup: RouteGroup.SYSTEM_OPERATIONS,
      scope: AuthScopes.readpromotion_campaigns,
      childrenRoutes: [
        {
          routerConfig: { path: '', Component: PromotionCampaignsAdmin },
          submenuDisplayName: 'Promotion Campaigns',
          submenuRoute: Routes.PROMOTION_CAMPAIGNS,
          scope: AuthScopes.readpromotion_campaigns,
        },
      ],
    },
    {
      routerConfig: { path: 'search', Component: Outlet }, // /search
      routeGroup: RouteGroup.SYSTEM_OPERATIONS,
      scope: AuthScopes.readsearch,
      childrenRoutes: [
        {
          routerConfig: { path: 'configuration', Component: SearchConfiguration },
          submenuDisplayName: 'Search Configuration',
          submenuRoute: Routes.SEARCH_CONFIGURATION,
          scope: AuthScopes.readsearch,
        },
      ],
    },
    {
      routerConfig: { path: 'plugins', Component: Outlet },
      routeGroup: RouteGroup.SYSTEM_OPERATIONS,
      scope: AuthScopes.readplugins,
      gambytAdminOnly: true,
      childrenRoutes: [
        {
          routerConfig: { path: '', Component: PluginConfiguration },
          submenuDisplayName: 'Plugin Configuration',
          submenuRoute: Routes.PLUGINS,
          scope: AuthScopes.readplugins,
        },
      ],
    },
    {
      routerConfig: { path: 'ticket-scan-search', Component: Outlet }, // /ticket-scan-search
      routeGroup: RouteGroup.SYSTEM_OPERATIONS,
      scope: AuthScopes.readplayer_scans,
      childrenRoutes: [
        {
          routerConfig: { path: '', Component: TicketScanSearch },
          submenuDisplayName: 'Ticket Scan Search',
          submenuRoute: Routes.TICKET_SCAN_SEARCH,
          scope: AuthScopes.readplayer_scans,
        },
      ],
    },
    {
      routerConfig: { path: 'scanner-scheduled-downtime', Component: Outlet },
      routeGroup: RouteGroup.SYSTEM_OPERATIONS,
      scope: AuthScopes.readticket_scanner_downtime,
      childrenRoutes: [
        {
          routerConfig: { path: '', Component: ScannerScheduledDowntime },
          submenuDisplayName: 'Scanner Scheduled Downtime',
          submenuRoute: Routes.SCANNER_SCHEDULED_DOWNTIME,
          scope: AuthScopes.readticket_scanner_downtime,
        },
      ],
    },
    {
      routerConfig: { path: 'reports', Component: Outlet }, // /reports
      routeGroup: RouteGroup.REPORTING,
      scope: AuthScopes.readreports,
      childrenRoutes: [
        {
          routerConfig: { path: '', Component: Outlet },
          scope: AuthScopes.readreports,
        },
        {
          routerConfig: { path: 'search-analytics', Component: SearchAnalytics }, // /reports/search-analytics
          submenuDisplayName: 'Search Analytics',
          submenuRoute: Routes.SEARCH_ANALYTICS,
          scope: AuthScopes.readreports,
        },
        {
          routerConfig: { path: 'claims-reports', Component: ClaimsReports }, // /reports/claims-reports
          submenuDisplayName: 'Claims Reports',
          submenuRoute: Routes.CLAIMS_REPORTS,
          scope: AuthScopes.readreports,
        },
      ],
    },
    {
      routerConfig: { path: 'users', Component: Users }, // /users
      routeGroup: RouteGroup.ADMIN,
      scope: AuthScopes.readusers,
      childrenRoutes: [
        {
          routerConfig: { path: '', Component: UsersList },
          submenuDisplayName: 'Users',
          submenuRoute: Routes.USERS,
          scope: AuthScopes.readusers,
        },
        {
          routerConfig: { path: ':authProviderId', Component: UpsertUser }, // /users/:authProviderId
          scope: AuthScopes.writeusers,
        },
        {
          routerConfig: { path: 'create', Component: UpsertUser }, // /users/create
          scope: AuthScopes.writeusers,
        },
        {
          routerConfig: { path: 'roles', Component: Outlet }, // /users/roles
          scope: AuthScopes.readroles,
          childrenRoutes: [
            {
              routerConfig: { path: '', Component: RolesList },
              scope: AuthScopes.readroles,
            },
            {
              routerConfig: { path: ':roleId', Component: UpsertRole }, // /users/roles/:roleId
              scope: AuthScopes.writeroles,
            },
            {
              routerConfig: { path: 'create', Component: UpsertRole }, // /users/roles/create
              scope: AuthScopes.writeroles,
            },
          ],
        },
      ],
    },
    {
      routerConfig: { path: 'imports', Component: Outlet }, // /imports
      routeGroup: RouteGroup.SYSTEM_OPERATIONS,
      scope: AuthScopes.readimports,
      gambytAdminOnly: true,
      childrenRoutes: [
        {
          routerConfig: { path: '', Component: ImportHistory },
          scope: AuthScopes.readimports,
          submenuDisplayName: 'Import History',
          submenuRoute: Routes.IMPORTS,
        },
        {
          routerConfig: { path: ':importId', Component: ImportHistoryDetails },
          scope: AuthScopes.readimports,
        },
      ],
    },
    {
      routerConfig: { path: 'customer-config', Component: Outlet }, // /customer-config
      routeGroup: RouteGroup.SYSTEM_OPERATIONS,
      scope: AuthScopes.readcustomer_config,
      gambytAdminOnly: true,
      childrenRoutes: [
        {
          routerConfig: { path: '', Component: CustomerConfig },
          submenuDisplayName: 'Customer Config',
          submenuRoute: Routes.CUSTOMER_CONFIG,
          scope: AuthScopes.readcustomer_config,
        },
      ],
    },
    {
      routerConfig: { path: 'api-keys', Component: ApiKeys }, // /api-keys
      routeGroup: RouteGroup.SYSTEM_OPERATIONS,
      scope: AuthScopes.readapi_keys,
      gambytAdminOnly: true,
      childrenRoutes: [
        {
          routerConfig: { path: '', Component: ApiKeysList },
          submenuDisplayName: 'API Keys',
          submenuRoute: Routes.API_KEYS,
          scope: AuthScopes.readapi_keys,
        },
        {
          routerConfig: { path: 'create', Component: CreateApiKey }, // /api-keys/create
          scope: AuthScopes.writeapi_keys,
        },
        {
          routerConfig: { path: ':keyId', Component: EditApiKey }, // /api-keys/:keyId
          scope: AuthScopes.writeapi_keys,
        },
      ],
    },
  ]
}
